import { type RefObject, useCallback, useEffect, useRef } from "react";

export interface UseInfiniteScrollTriggerOptions {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
}

export interface UseInfiniteScrollTriggerResult<T extends HTMLElement> {
  ref: RefObject<T | null>;
  /** Latched `onLoadMore`: no-op while a load is already pending. */
  requestLoadMore: () => void;
}

// Re-arm window for loads that never report a loading cycle (e.g. a no-op
// showMore on the last page), so one swallowed call can't brick the trigger.
const WATCHDOG_MS = 3000;

export function useInfiniteScrollTrigger<T extends HTMLElement = HTMLDivElement>({
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = "200px",
}: UseInfiniteScrollTriggerOptions): UseInfiniteScrollTriggerResult<T> {
  const ref = useRef<T>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  // `isLoading` only flips after the search dispatches and React re-renders,
  // so an intersection burst (fast scroll) or button spam can request the same
  // page many times inside that window. The latch is set synchronously at call
  // time and released on the next committed render where `isLoading` is false
  // — cache-served loads never render a loading state, so requiring a full
  // false → true → false cycle would freeze the trigger on them. A duplicate
  // that slips through after an early release is byte-identical and collapsed
  // by the transporter's in-flight dedup. The watchdog (at most one live, by
  // construction) re-arms the latch after a load that never commits a render
  // at all (e.g. a no-op showMore on the last page).
  const pendingRef = useRef(false);
  const watchdogRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  });

  useEffect(() => {
    pendingRef.current = isLoading;
    if (isLoading) clearTimeout(watchdogRef.current);
  });

  useEffect(() => () => clearTimeout(watchdogRef.current), []);

  const requestLoadMore = useCallback(() => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    clearTimeout(watchdogRef.current);
    watchdogRef.current = setTimeout(() => {
      pendingRef.current = false;
    }, WATCHDOG_MS);
    onLoadMoreRef.current();
  }, []);

  useEffect(() => {
    const node = ref.current;
    if (!node || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) requestLoadMore();
        }
      },
      { rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, rootMargin, requestLoadMore]);

  return { ref, requestLoadMore };
}
